import { useState, useRef, useEffect, useMemo } from 'react';
import { Input, Button, Typography, Empty, Tag, Card } from 'antd';
import { SendOutlined, ShoppingOutlined, DollarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useMessages, IOrder } from '../hooks';
import styled from 'styled-components';

const { Text } = Typography;
const { TextArea } = Input;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const OrderInfoCard = styled(Card)`
  margin: 12px 16px 0 16px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  flex-shrink: 0;
  
  .ant-card-body {
    padding: 16px;
  }
  
  .order-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .order-title {
    color: #262626;
    font-weight: 700;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    .anticon {
      color: #FF6B35;
    }
  }
  
  .order-meta {
    display: flex;
    gap: 16px;
    margin-bottom: 12px;
    color: #595959;
    font-size: 13px;
    
    div {
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }

  .products-list {
    margin-top: 12px;
    border-top: 1px solid #f0f0f0;
    padding-top: 12px;
  }

  .product-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
    font-size: 13px;
    
    &:last-child {
      margin-bottom: 0;
    }

    .product-name {
      color: #262626;
      flex: 1;
    }
    
    .product-price {
      font-weight: 600;
      margin-left: 12px;
    }
  }
`;

const MessagesList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  background: #f5f5f5;
  position: relative;
  
  /* Ocultar scrollbar pero mantener funcionalidad */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const MessageBubble = styled.div<{ isAdmin: boolean }>`
  display: flex;
  justify-content: ${props => props.isAdmin ? 'flex-end' : 'flex-start'};
  margin-bottom: 12px;
  
  .message-content {
    max-width: 70%;
    padding: 10px 14px;
    border-radius: 18px;
    background: ${props => props.isAdmin ? '#1890ff' : '#fff'};
    color: ${props => props.isAdmin ? '#fff' : '#000'};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    
    .message-time {
      font-size: 11px;
      opacity: 0.7;
      margin-top: 4px;
    }
  }
`;

const InputContainer = styled.div`
  padding: 16px;
  border-top: 1px solid #e8e8e8;
  display: flex;
  gap: 8px;
  background: #fff;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

interface ChatWindowProps {
  userId: number;
  userName: string;
}

const getOrderStatusText = (statusId: number): string => {
  const statuses: Record<number, string> = {
    1: 'Pendiente',
    2: 'Confirmado',
    3: 'En proceso',
    5: 'En preparación',
    8: 'En camino',
    9: 'Entregado',
    10: 'Cancelado',
  };
  return statuses[statusId] || 'Desconocido';
};

const getOrderStatusColor = (statusId: number): string => {
  const colors: Record<number, string> = {
    1: 'orange',
    2: 'blue',
    3: 'processing',
    5: 'purple',
    8: 'cyan',
    9: 'green',
    10: 'red',
  };
  return colors[statusId] || 'default';
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const ChatWindow = ({ userId, userName }: ChatWindowProps) => {
  const [inputValue, setInputValue] = useState('');
  const { messages, isLoading, isSending, sendMessage } = useMessages(userId);
  const lastMessageIdRef = useRef<number | null>(null);

  // Obtener información del pedido del primer mensaje que tenga order_id
  const orderInfo: IOrder | null = useMemo(() => {
    const messageWithOrder = messages.find(m => m.order);
    return messageWithOrder?.order || null;
  }, [messages]);

  // Verificar si el pedido está finalizado (Entregado o Cancelado)
  const isOrderFinished = useMemo(() => {
    if (!orderInfo) return false;
    // Estados finalizados: 9 (Entregado), 10 (Cancelado), 6 (Entregado alternativo)
    return [6, 9, 10].includes(orderInfo.order_status_id);
  }, [orderInfo]);

  // Variable para el AudioContext (se inicializa después de la primera interacción)
  const audioContextRef = useRef<AudioContext | null>(null);

  // Inicializar AudioContext después de la primera interacción del usuario
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (error) {
        // Silenciar el error
      }
    }
    return audioContextRef.current;
  };

  // Función para reproducir sonido de notificación
  const playNotificationSound = () => {
    try {
      const ctx = initAudioContext();
      if (!ctx) return;

      // Si el contexto está suspendido, intentar reanudarlo
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {
          return;
        });
      }

      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (error) {
      // Silenciar el error
    }
  };

  // Inicializar AudioContext cuando el usuario interactúe con el componente
  useEffect(() => {
    const initOnInteraction = () => {
      initAudioContext();
      window.removeEventListener('click', initOnInteraction);
      window.removeEventListener('touchstart', initOnInteraction);
      window.removeEventListener('keydown', initOnInteraction);
    };
    
    window.addEventListener('click', initOnInteraction, { once: true });
    window.addEventListener('touchstart', initOnInteraction, { once: true });
    window.addEventListener('keydown', initOnInteraction, { once: true });

    return () => {
      window.removeEventListener('click', initOnInteraction);
      window.removeEventListener('touchstart', initOnInteraction);
      window.removeEventListener('keydown', initOnInteraction);
    };
  }, []);

  // Detectar nuevos mensajes y reproducir sonido
  useEffect(() => {
    if (messages.length > 0 && userId) {
      const lastMessage = messages[messages.length - 1];
      
      // Si hay un nuevo mensaje que no sea del admin
      if (lastMessage.id !== lastMessageIdRef.current && !lastMessage.is_admin) {
        lastMessageIdRef.current = lastMessage.id;
        playNotificationSound();
      } else if (lastMessageIdRef.current === null) {
        // Primera carga, guardar el último ID
        lastMessageIdRef.current = lastMessage.id;
      }
    }
  }, [messages, userId]);

  // Resetear contadores cuando cambia la conversación
  useEffect(() => {
    lastMessageIdRef.current = null;
  }, [userId]);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending || isOrderFinished) return;
    
    const messageText = inputValue;
    setInputValue('');
    await sendMessage(messageText, true); // true porque es admin
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!userId) {
    return (
      <ChatContainer>
        <Empty description="Selecciona una conversación para comenzar" />
      </ChatContainer>
    );
  }

  return (
    <ChatContainer>
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #e8e8e8', 
        background: '#fff', 
        flexShrink: 0,
        position: 'relative',
        zIndex: 1
      }}>
        <Text strong style={{ fontSize: 16 }}>
          Chat con {userName}
        </Text>
      </div>

      {/* Información del pedido relacionado */}
      {orderInfo && (
        <div style={{ flexShrink: 0, overflow: 'hidden' }}>
          <OrderInfoCard bordered={false}>
          <div className="order-header">
            <div className="order-title">
              <ShoppingOutlined />
              Pedido #{orderInfo.id}
            </div>
            <Tag color={getOrderStatusColor(orderInfo.order_status_id)}>
              {getOrderStatusText(orderInfo.order_status_id)}
            </Tag>
          </div>
          
          <div className="order-meta">
            <div>
              <DollarOutlined style={{ color: '#52c41a' }} />
              <span style={{ fontWeight: 600 }}>{formatCurrency(orderInfo.total_price)}</span>
            </div>
            <div>
              <ClockCircleOutlined style={{ color: '#1890ff' }} />
              {new Date(orderInfo.createdAt).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
            {orderInfo.confirmation_code && (
              <div>
                <Tag color="orange">Código: {orderInfo.confirmation_code}</Tag>
              </div>
            )}
          </div>

          {orderInfo.order_details && orderInfo.order_details.length > 0 && (
            <div className="products-list">
              {orderInfo.order_details.map((item) => (
                <div key={item.id} className="product-item">
                  <span className="product-name">
                    <span style={{ fontWeight: 600, marginRight: 4 }}>{item.quantity}x</span>
                    {item.product_name}
                  </span>
                  <span className="product-price">
                    {formatCurrency(item.total_price_tax_incl)}
                  </span>
                </div>
              ))}
            </div>
          )}
          </OrderInfoCard>
        </div>
      )}

      <MessagesList>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Text type="secondary">Cargando mensajes...</Text>
          </div>
        ) : messages.length === 0 ? (
          <Empty description="No hay mensajes aún" />
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble key={msg.id} isAdmin={msg.is_admin || false}>
                <div className="message-content">
                  <div>{msg.message}</div>
                  <div className="message-time">
                    {msg.createdAt 
                      ? new Date(msg.createdAt).toLocaleTimeString('es-ES', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })
                      : ''}
                  </div>
                </div>
              </MessageBubble>
            ))}
            {isOrderFinished && (
              <div style={{
                textAlign: 'center',
                padding: '12px 16px',
                margin: '12px auto',
                maxWidth: '80%',
                background: '#fff7e6',
                border: '1px solid #ffe58f',
                borderRadius: '8px',
                color: '#ad6800',
                fontSize: '13px',
                fontWeight: 500
              }}>
                ⚠️ Conversación cerrada: El pedido ha sido {orderInfo?.order_status_id === 10 ? 'cancelado' : 'finalizado'}
              </div>
            )}
          </>
        )}
      </MessagesList>

      <InputContainer>
        {isOrderFinished ? (
          <div style={{ 
            width: '100%', 
            padding: '12px 16px', 
            background: '#fff7e6', 
            border: '1px solid #ffe58f',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#ad6800',
            fontSize: '14px',
            fontWeight: 500
          }}>
            Esta conversación está cerrada porque el pedido ha sido {orderInfo?.order_status_id === 10 ? 'cancelado' : 'finalizado'}.
          </div>
        ) : (
          <>
            <TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe un mensaje..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={isSending}
              disabled={!inputValue.trim()}
            >
              Enviar
            </Button>
          </>
        )}
      </InputContainer>
    </ChatContainer>
  );
};
